import {createReducer} from '@reduxjs/toolkit';

import {adapter, initialState} from './state';
import {
    addOptimisticMessageAction, addReceivedMessageAction,
    clearAllAction,
    clearErrorsAction,
    getAllAction, sendMessageAction,
    setSelectedIdAction, updateMessageAction,
} from './actions';

export const reducer = createReducer(initialState, builder =>
    builder
        /** HANDLE ACTION ====================================== */
        .addCase(getAllAction.pending, (state, action) => {
            // Snapshot the ids known right now so fulfilment can tell a stale
            // message (present at start, gone from the response) apart from one
            // that arrived while the request was in flight (F13).
            const {customerId} = action.meta.arg;
            const all = adapter.getSelectors().selectAll(state);
            state.pendingFetchIds = (customerId
                ? all.filter((chat) => chat.customerId === customerId)
                : all).map((chat) => chat.id);
            state.isFetching = true;
            state.isSending = false;
        })
        .addCase(getAllAction.fulfilled, (state, action) => {
            const { results, customerId } = action.payload;
            const { messages } = results;
            let newState = adapter.getInitialState({ ...state, isFetching: false, pendingFetchIds: undefined });

            if (!customerId) {
                // Unscoped fetch: replace everything with the response.
                return adapter.setAll(newState, Object.values(messages));
            }

            const idsAtFetchStart = new Set(state.pendingFetchIds ?? []);
            const newChatIdsSet = new Set(Object.values(messages).map((chat) => chat.id));
            const existingChats = adapter.getSelectors().selectAll(state)
                .filter((chat) => chat.customerId === customerId);

            for (const chat of existingChats) {
                // Never prune an optimistic/in-flight send (negative id or
                // pending) or a message that arrived after this fetch began
                // (absent from the start snapshot) — it predates the server's
                // response only by accident of timing, not because it's stale.
                if (chat.pending || chat.id < 0) continue;
                if (!idsAtFetchStart.has(chat.id)) continue;
                if (!newChatIdsSet.has(chat.id)) {
                    newState = adapter.removeOne(newState, chat.id);
                }
            }

            return adapter.upsertMany(newState, Object.values(messages));
        })
        .addCase(getAllAction.rejected, state => {
            return {...state, isFetching: false, isSending: false, pendingFetchIds: undefined};
        })

        /** HANDLE ACTION ====================================== */
        .addCase(sendMessageAction.pending, state => {
            return {...state, isSending: true};
        })
        .addCase(sendMessageAction.fulfilled, (state, action) => {
            return { ...state, isSending: false };
        })
        .addCase(sendMessageAction.rejected, (state, action) => {
            // The send failed, so the optimistic bubble is a phantom — remove it
            // by localId. Chats are persisted, so leaving it would survive
            // restarts until the next successful getAll pruned it (F12).
            const {localId} = action.meta.arg;
            const optimistic = Object.values(state.entities).find((msg) => msg?.localId === localId);
            if (optimistic) {
                adapter.removeOne(state, optimistic.id);
            }
            state.isSending = false;
        })

        .addCase(addOptimisticMessageAction, (state, action) => {
            adapter.addOne(state, action.payload);
        })

        // When the API returns, update the optimistic message using localId.
        .addCase(updateMessageAction, (state, action) => {
            const { localId, chatMessage } = action.payload;

            if (!chatMessage || !chatMessage.id) {
                console.warn('Invalid message:', chatMessage);
                return;
            }

            // Find the optimistic message by its unique localId
            const optimisticMessage = Object.values(state.entities)
                .find((msg) => msg?.localId === localId);

            if (optimisticMessage) {
                // Remove the optimistic message (using its unique temporary id)
                adapter.removeOne(state, optimisticMessage.id);
            }

            // Add the confirmed message with its real id
            adapter.addOne(state, { ...chatMessage, pending: false });
        })

        .addCase(addReceivedMessageAction, (state, action) => {
            const chatMessage = action.payload;

            if (!chatMessage || !chatMessage.id) {
                console.warn('Invalid received message:', chatMessage);
                return;
            }

            // Ensure the message is not duplicated
            if (!state.entities[chatMessage.id]) {
                adapter.addOne(state, { ...chatMessage, pending: false });
            }
        })

        /** HANDLE NON-API ACTIONS ====================================== */
        .addCase(setSelectedIdAction.fulfilled, (state, action) => {
            const {selectedId} = action.payload;
            return {...state, selectedId: selectedId};
        })
        .addCase(clearAllAction.fulfilled, () => adapter.removeAll({...initialState}))
        .addCase(clearErrorsAction.fulfilled, state => ({...state, errorMessage: undefined}))
);
